import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

/* ── Design tokens — identisch mit Landing Page ── */
const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
  bg:     '#f7f7f7',
  font:   "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif",
};

const PLANS = [
  {
    key:   'starter',
    name:  'Starter',
    price: '29',
    desc:  'Für private Vermieter mit einem kleinen Portfolio.',
    units: 'Bis zu 10 Einheiten',
    features: [
      'Bis zu 10 Einheiten',
      'Unbegrenzte Mieter',
      'Zahlungsverfolgung',
      'Automatisches Mahnwesen',
      'PDF Export',
    ],
  },
  {
    key:       'professional',
    name:      'Professional',
    price:     '79',
    desc:      'Für wachsende Portfolios mit mehr Anforderungen.',
    units:     'Bis zu 100 Einheiten',
    highlight: true,
    features: [
      'Bis zu 100 Einheiten',
      'Alles aus Starter',
      'Betriebskostenabrechnung',
      'Mieterportal',
      'Dokumente & Berichte',
      'Prioritäts-Support',
    ],
  },
  {
    key:   'enterprise',
    name:  'Enterprise',
    price: '199',
    desc:  'Für professionelle Verwalter und Teams.',
    units: 'Unbegrenzte Einheiten',
    features: [
      'Unbegrenzte Einheiten',
      'Alles aus Professional',
      'Mehrere Nutzer / Team',
      'API-Zugang',
      'Dedizierter Account-Manager',
      'SLA-Garantie',
    ],
  },
];

const IcoCheck = ({ color = '#16a34a' }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

export default function PlanSelect() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const [loading, setLoading] = useState(null); // planKey | null
  const [error, setError]     = useState('');

  async function handleSelect(planKey) {
    if (!user) { navigate('/login'); return; }
    setLoading(planKey);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Sitzung abgelaufen — bitte neu einloggen.');

      const res = await fetch('/api/create-checkout-session', {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ plan: planKey }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Unbekannter Fehler');

      window.location.href = json.url;
    } catch (err) {
      setError(err.message);
      setLoading(null);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: T.font, color: T.ink }}>

      {/* ── Minimal Header ── */}
      <header style={{
        background: 'white', borderBottom: `1px solid ${T.border}`,
        padding: '0 clamp(16px, 5vw, 56px)', height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <span style={{ fontSize: 15, fontWeight: 900, letterSpacing: '2.5px', textTransform: 'uppercase' }}>
          IMMO<span style={{ color: T.ink }}>HQ</span>
        </span>
        <Link to="/dashboard" style={{
          fontSize: 12, color: T.muted, textDecoration: 'none',
          fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4,
        }}>
          Erst anschauen →
        </Link>
      </header>

      <div style={{ maxWidth: 1040, margin: '0 auto', padding: 'clamp(40px, 7vw, 80px) clamp(16px, 5vw, 40px)' }}>

        {/* ── Titel ── */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(36px, 5vw, 56px)' }}>

          {/* Trial-Pill */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: '#f5f5f5', border: `1px solid ${T.border}`,
            borderRadius: 99, padding: '5px 14px', marginBottom: 20,
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.ink }} />
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: T.ink }}>
              14 Tage kostenlos testen
            </span>
          </div>

          <h1 style={{
            fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: 900,
            letterSpacing: '-2px', lineHeight: 1.1, margin: '0 0 14px',
          }}>
            Wähle deinen Plan
          </h1>
          <p style={{ fontSize: 15, color: T.muted, maxWidth: 440, margin: '0 auto', lineHeight: 1.65 }}>
            Keine Kreditkarte sofort nötig — du wirst nach 14 Tagen automatisch belastet.
            Jederzeit kündbar.
          </p>
        </div>

        {/* ── Plan Cards ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 16,
          alignItems: 'start',
        }}>
          {PLANS.map((plan) => {
            const isHighlight = plan.highlight;
            const isLoading   = loading === plan.key;

            return (
              <div
                key={plan.key}
                style={{
                  background:   isHighlight ? T.ink : 'white',
                  border:       `1.5px solid ${isHighlight ? T.ink : T.border}`,
                  borderRadius: 20,
                  padding:      'clamp(28px, 4vw, 40px)',
                  position:     'relative',
                  boxShadow:    isHighlight
                    ? '0 16px 48px rgba(17,24,39,0.22)'
                    : '0 2px 8px rgba(0,0,0,0.04)',
                  transform:    isHighlight ? 'scale(1.02)' : 'none',
                }}
              >
                {/* Beliebt Badge */}
                {isHighlight && (
                  <div style={{
                    position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                    background: T.ink, color: 'white',
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase',
                    padding: '4px 16px', borderRadius: 99,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    whiteSpace: 'nowrap',
                  }}>
                    Beliebt
                  </div>
                )}

                {/* Plan-Name */}
                <div style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase',
                  color: isHighlight ? 'rgba(255,255,255,0.4)' : T.muted,
                  marginBottom: 12,
                }}>
                  {plan.name}
                </div>

                {/* Preis */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8 }}>
                  <span style={{
                    fontSize: 'clamp(36px, 4vw, 48px)', fontWeight: 900,
                    letterSpacing: '-2px', lineHeight: 1,
                    color: isHighlight ? 'white' : T.ink,
                  }}>
                    {plan.price} €
                  </span>
                  <span style={{ fontSize: 13, color: isHighlight ? 'rgba(255,255,255,0.35)' : T.muted }}>
                    / Monat
                  </span>
                </div>

                {/* Einheiten-Info */}
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  background: isHighlight ? 'rgba(0,0,0,0.05)' : '#f5f5f5',
                  border: `1px solid ${isHighlight ? 'rgba(0,0,0,0.1)' : T.border}`,
                  borderRadius: 99, padding: '3px 10px', marginBottom: 14,
                }}>
                  <span style={{
                    fontSize: 11, fontWeight: 600,
                    color: isHighlight ? '#93c5fd' : T.ink,
                  }}>
                    {plan.units}
                  </span>
                </div>

                <p style={{
                  fontSize: 13, color: isHighlight ? 'rgba(255,255,255,0.45)' : T.muted,
                  lineHeight: 1.6, margin: '0 0 24px',
                }}>
                  {plan.desc}
                </p>

                {/* Feature-Liste */}
                <ul style={{
                  listStyle: 'none', padding: 0,
                  margin: '0 0 28px',
                  display: 'flex', flexDirection: 'column', gap: 9,
                }}>
                  {plan.features.map((f) => (
                    <li key={f} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 9,
                      fontSize: 13,
                      color: isHighlight ? 'rgba(255,255,255,0.82)' : T.ink,
                    }}>
                      <span style={{ flexShrink: 0, marginTop: 1 }}>
                        <IcoCheck color={isHighlight ? 'rgba(255,255,255,0.55)' : '#16a34a'} />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                {/* Trial-Hinweis */}
                <div style={{
                  fontSize: 11, fontWeight: 600,
                  color: isHighlight ? 'rgba(255,255,255,0.35)' : T.muted,
                  marginBottom: 14, textAlign: 'center',
                }}>
                  {plan.key === 'enterprise'
                    ? 'Preis auf Anfrage · individuell'
                    : `14 Tage gratis · danach ${plan.price} €/Monat`
                  }
                </div>

                {/* CTA Button */}
                {plan.key === 'enterprise' ? (
                  <a
                    href="mailto:support@immohq.de?subject=Enterprise%20Anfrage"
                    style={{
                      display: 'block', width: '100%', padding: '13px 0', textAlign: 'center',
                      background: T.ink, color: 'white', borderRadius: 10,
                      fontSize: 13, fontWeight: 700, letterSpacing: '0.2px',
                      textDecoration: 'none', boxSizing: 'border-box',
                      boxShadow: '0 2px 8px rgba(17,24,39,0.18)',
                      fontFamily: T.font,
                    }}
                  >
                    Vertrieb kontaktieren →
                  </a>
                ) : (
                  <button
                    onClick={() => handleSelect(plan.key)}
                    disabled={!!loading}
                    style={{
                      width: '100%', padding: '13px 0',
                      background: isHighlight ? T.ink : T.ink,
                      color: 'white', border: 'none', borderRadius: 10,
                      fontSize: 13, fontWeight: 700, letterSpacing: '0.2px',
                      cursor: loading ? 'wait' : 'pointer',
                      fontFamily: T.font,
                      opacity: loading && !isLoading ? 0.5 : 1,
                      boxShadow: isHighlight ? '0 4px 16px rgba(0,0,0,0.18)' : '0 2px 8px rgba(17,24,39,0.18)',
                      transition: 'opacity 0.15s, transform 0.1s',
                    }}
                    onMouseEnter={e => { if (!loading) e.currentTarget.style.opacity = '0.88'; }}
                    onMouseLeave={e => { if (!loading) e.currentTarget.style.opacity = '1'; }}
                  >
                    {isLoading ? 'Weiterleitung zu Stripe…' : `${plan.name} starten →`}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Fehler ── */}
        {error && (
          <div style={{
            marginTop: 24, padding: '14px 20px',
            background: '#fef2f2', border: '1px solid #fca5a5',
            borderRadius: 10, fontSize: 13, color: '#b91c1c',
            maxWidth: 600, margin: '24px auto 0',
          }}>
            <strong>Fehler:</strong> {error}
          </div>
        )}

        {/* ── Info-Zeile ── */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 'clamp(16px, 4vw, 40px)',
          flexWrap: 'wrap', marginTop: 36,
        }}>
          {[
            'Keine Kreditkarte sofort nötig',
            'DSGVO-konform',
            'Jederzeit kündbar',
          ].map(item => (
            <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <IcoCheck color="#16a34a" />
              <span style={{ fontSize: 12, color: T.muted, fontWeight: 500 }}>{item}</span>
            </div>
          ))}
        </div>

        {/* ── Stripe-Hinweis (Test-Modus) ── */}
        <div style={{
          textAlign: 'center', marginTop: 28,
          padding: '10px 20px', background: '#fefce8',
          border: '1px solid #fef08a', borderRadius: 8,
          display: 'inline-block', margin: '28px auto 0',
          width: '100%', maxWidth: 500,
          boxSizing: 'border-box',
        }}>
          <span style={{ fontSize: 12, color: '#92400e', fontWeight: 600 }}>
            Test-Modus aktiv —
          </span>
          <span style={{ fontSize: 12, color: '#a16207' }}>
            {' '}Testkarte: 4242 4242 4242 4242 · Ablauf: beliebig · CVC: beliebig
          </span>
        </div>

        {/* ── Skip-Link ── */}
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Link to="/dashboard" style={{
            fontSize: 12, color: T.muted, textDecoration: 'none', fontWeight: 500,
          }}>
            Später entscheiden — Dashboard anschauen →
          </Link>
        </div>

      </div>
    </div>
  );
}
